import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { EditActions, editActions } from "../../actions/editActions";
import NumberField from "../../components/Editing/NumberField";

type StateProps = {
  editedEnumeration: Api.BaseEnumeration;
  storedEnumeration: Api.BaseEnumeration;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
  dataFieldName: keyof State.PdApi;
};
interface ErrorStatus {
  errorStatus: boolean;
  errorMsg: string;
}

const baseEnumFields = nameofFactory<Api.BaseEnumeration>();

export type EditEnumProps = StateProps & DispatchProps & OwnProps;

export class EditEnum extends React.Component<EditEnumProps> {
  render(): JSX.Element {
    const {
      editedEnumeration,
      storedEnumeration,
      activeUser,
      toggleModal,
      dataFieldName,
      pdApi: { electrifications }
    } = this.props;
    const isNew = !storedEnumeration;
    const permission = hasWriteAccess(
      activeUser,
      dataFieldName === "electrifications" ? undefined : "Lift" // Other than electrifications, all enumerations belong to lift group
    );

    let hasLinkedDomains = false;
    let linkedTooltip = ": Item linked to child components";

    if (dataFieldName === "electrifications" && !isNew) {
      const selectedElectrification = electrifications?.find(electrification => electrification.enumValue === editedEnumeration.enumValue);
      hasLinkedDomains = (selectedElectrification?.domains?.length ?? 0) > 0;
    } else if (dataFieldName === "eventClasses" && !isNew) {
      hasLinkedDomains = this.props.pdApi.messageTypes?.some(
        mt => mt.mapping?.eventClass?.enumValue === editedEnumeration.enumValue
      ) ?? false;
      linkedTooltip = ": Event Class is linked to one or more Message Types";
    } else if (dataFieldName === "eventTypes" && !isNew) {
      hasLinkedDomains = this.props.pdApi.messageTypes?.some(
        mt => mt.mapping?.eventTypes?.some(et => et.enumValue === editedEnumeration.enumValue)
      ) ?? false;
      linkedTooltip = ": Event Type is linked to one or more Message Types";
    }
    const errorenumName = this.isFieldValid(baseEnumFields("enumName"));
    const errorenumValue = this.isFieldValid(baseEnumFields("enumValue"));
    
    if (storedEnumeration) {
      storedEnumeration.comment = storedEnumeration.comment ? storedEnumeration.comment : "";
    }

    return (
      <Editor
        itemName={dataFieldName === "electrifications" ? "Electrification" : "Enumeration"}
        originalObject={storedEnumeration}
        editedObject={editedEnumeration}
        hasWritePermission={permission}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update(dataFieldName as Api.OptionsGrud)}
        create={() => this.props.editActions.add(dataFieldName as Api.OptionsGrud)}
        remove={{ callback: toggleModal
          , disabled: hasLinkedDomains
          , disabledTooltip: linkedTooltip }}
      >
        <NumberField
          id="enumValue"
          label="Value"
          value={editedEnumeration.enumValue}
          isError={errorenumValue.errorStatus}
          errorMessage={errorenumValue.errorMsg}
          readonly={!isNew}
          onChange={enumValue =>
            this.props.editActions.setEdited({ ...editedEnumeration, enumValue: enumValue })
          }
        />
        <TextField
          id="enumName"
          label="Enumeration Name"
          value={editedEnumeration.enumName}
          isError={errorenumName.errorStatus}
          errorMessage={errorenumName.errorMsg}
          readonly={dataFieldName === "electrifications" && !isNew ? true : !permission}
          onChange={enumName =>
            this.props.editActions.setEdited({ ...editedEnumeration, enumName: enumName.trim() })
          }
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedEnumeration.comment}
          isError={!this.isFieldValid(baseEnumFields("comment"))}
          readonly={!permission}
          onChange={comment =>
            this.props.editActions.setEdited({ ...editedEnumeration, comment: comment.trim() })
          }
        />
      </Editor>
    );
  }

  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

  private checkSpecialCharacters = (str: string): boolean =>
    !/^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_]+$/.test(str);

  private isFieldValid = (field: string): ErrorStatus => {
    const { editedEnumeration, storedEnumeration, pdApi } = this.props;
    const isNew = !storedEnumeration;
    switch (field) {
      case baseEnumFields("enumName"): {
        const { enumName } = editedEnumeration;

        if (this.isEmptyString(enumName)) {
          return { errorStatus: true, errorMsg: "The enumeration name cannot be empty." };
        } else if (
          this.checkSpecialCharacters(enumName) &&
          this.props.dataFieldName !== "electrifications"
        ) {
          return {
            errorStatus: true,
            errorMsg: "The enumeration name must contain only alphabets numbers and underscores."
          };
        } else {
          return _.values(pdApi[this.props.dataFieldName]).find(
            enumeration =>
              (isNew || enumeration.enumValue !== editedEnumeration.enumValue) &&
              enumeration.enumName.trim().toLowerCase() ===
                editedEnumeration.enumName.trim().toLowerCase()
          )
            ? { errorStatus: true, errorMsg: "The enumeration name must be unique." }
            : { errorStatus: false, errorMsg: "" };
        }
      }

      case baseEnumFields("enumValue"):
        if (this.props.storedEnumeration) {
          return { errorStatus: false, errorMsg: "" };
        }
        return _.values(pdApi[this.props.dataFieldName]).find(
          enumeration => enumeration.enumValue === editedEnumeration.enumValue
        )
          ? { errorStatus: true, errorMsg: "The value must be unique." }
          : { errorStatus: false, errorMsg: "" };

      default:
        return { errorStatus: false, errorMsg: "" };
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedEnumeration } = this.props;
    const temp = _.keys(editedEnumeration);
    for (const key of temp) {
      if (this.isFieldValid(key).errorStatus) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedEnumeration: state.editView.editedObject,
  storedEnumeration: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditEnum);


